<template>
  <div class="tet">
    <div class="tet-inner">
      <!-- 游戏板 -->
      <div class="tet-board">
        <div
            v-for="(cell, i) in displayBoard"
            :key="i"
            class="tet-cell"
            :class="cell.v ? (cell.ghost ? 'ghost g' + cell.v : 'c' + cell.v) : ''"></div>
        <div v-if="paused && !over" class="tet-over">
          <div class="tet-over-emoji">⏸️</div>
          <div class="tet-over-text">已暂停</div>
          <button type="button" class="tet-btn" @click="togglePause">继续游戏</button>
        </div>
        <div v-if="over" class="tet-over">
          <div class="tet-over-emoji">😢</div>
          <div class="tet-over-text">游戏结束</div>
          <div class="tet-over-score">得分 {{ score }} · 最高 {{ best }}</div>
          <button type="button" class="tet-btn" @click="restart">再来一局</button>
        </div>
      </div>

      <!-- 侧栏 -->
      <div class="tet-side">
        <div class="tet-hold" :class="{ locked: !canHold && !over }">
          <div class="tet-side-label">保留区</div>
          <div class="tet-next-grid tet-hold-grid">
            <div
                v-for="(cell, i) in holdPreview"
                :key="i"
                class="tet-mini-cell"
                :class="holdColor ? 'c' + holdColor : ''"></div>
          </div>
        </div>
        <div class="tet-next">
          <div class="tet-side-label">下一块</div>
          <div class="tet-next-grid">
            <div
                v-for="(cell, i) in nextPreview"
                :key="i"
                class="tet-mini-cell"
                :class="cell ? 'c' + nextColor : ''"></div>
          </div>
        </div>
        <div class="tet-stat">
          <span>得分</span>
          <b>{{ score }}</b>
        </div>
        <div class="tet-stat">
          <span>最高分</span>
          <b>{{ best }}</b>
        </div>
        <div class="tet-stat">
          <span>消行</span>
          <b>{{ lines }}</b>
        </div>
        <div class="tet-stat">
          <span>等级</span>
          <b>{{ level }}</b>
        </div>
        <button type="button" class="tet-btn tet-restart" @click="restart">重新开始</button>
      </div>
    </div>

    <!-- 触屏 / 鼠标控制面板 -->
    <div class="tet-pad">
      <button type="button" class="tet-pad-btn" title="保留 (C)" @click="holdPiece">⬆️ 保留</button>
      <button type="button" class="tet-pad-btn" title="左移 (←)" @click="move(-1)">◀</button>
      <button type="button" class="tet-pad-btn" title="旋转 (↑)" @click="rotateCur">⟳</button>
      <button type="button" class="tet-pad-btn" title="右移 (→)" @click="move(1)">▶</button>
      <button type="button" class="tet-pad-btn" title="加速下落 (↓)" @click="softDrop">▼</button>
      <button type="button" class="tet-pad-btn" title="落底 (空格)" @click="hardDrop">⤓</button>
      <button type="button" class="tet-pad-btn" title="暂停 (P)" @click="togglePause">{{ paused ? '▶' : '⏸' }}</button>
    </div>
    <p class="tet-tip">← → 移动 · ↑ 旋转 · ↓ 加速 · 空格 落底 · C 保留 · P 暂停</p>
  </div>
</template>

<script>
/* =====================================================
 * 粉甜俄罗斯方块（复刻 y7x7 PlayStudios 风格）
 * 7 种方块 + 旋转/移动/加速/硬降 + 下一块 + 保留区 + 投影
 * 计分 / 等级 / 最高分（localStorage）/ 暂停 / 触屏按钮
 * ===================================================== */
const ROWS = 20
const COLS = 10
const SHAPES = {
  I: [[0, 0, 0, 0], [1, 1, 1, 1], [0, 0, 0, 0], [0, 0, 0, 0]],
  O: [[1, 1], [1, 1]],
  T: [[0, 1, 0], [1, 1, 1], [0, 0, 0]],
  S: [[0, 1, 1], [1, 1, 0], [0, 0, 0]],
  Z: [[1, 1, 0], [0, 1, 1], [0, 0, 0]],
  J: [[1, 0, 0], [1, 1, 1], [0, 0, 0]],
  L: [[0, 0, 1], [1, 1, 1], [0, 0, 0]]
}
const TYPES = Object.keys(SHAPES)
const COLOR = { I: 1, O: 2, T: 3, S: 4, Z: 5, J: 6, L: 7 }
const SCORE_LINE = [0, 100, 300, 600, 1000]
const BEST_KEY = 'pink_tetris_best'

export default {
  name: 'Tetris',
  data () {
    return {
      board: [],
      cur: null,
      next: null,
      holdType: null,
      canHold: true,
      score: 0,
      lines: 0,
      level: 1,
      best: 0,
      over: false,
      paused: false,
      dropTimer: null
    }
  },
  computed: {
    nextColor () {
      return this.next ? COLOR[this.next.type] : 1
    },
    holdColor () {
      return this.holdType ? COLOR[this.holdType] : 0
    },
    nextPreview () {
      const grid = Array(12).fill(0)
      if (!this.next) return grid
      const shape = this.next.shape
      for (let r = 0; r < shape.length; r++) {
        for (let c = 0; c < shape[r].length; c++) {
          if (shape[r][c] && r < 3 && c < 4) grid[r * 4 + c] = 1
        }
      }
      return grid
    },
    holdPreview () {
      const grid = Array(12).fill(0)
      if (!this.holdType) return grid
      const shape = SHAPES[this.holdType]
      for (let r = 0; r < shape.length; r++) {
        for (let c = 0; c < shape[r].length; c++) {
          if (shape[r][c] && r < 3 && c < 4) grid[r * 4 + c] = 1
        }
      }
      return grid
    },
    displayBoard () {
      const view = []
      for (let i = 0; i < this.board.length; i++) {
        view.push({ v: this.board[i], ghost: false })
      }
      if (this.cur && !this.over && !this.paused) {
        const shape = this.cur.shape
        const color = COLOR[this.cur.type]
        const gy = this.dropDistance()
        for (let r = 0; r < shape.length; r++) {
          for (let c = 0; c < shape[r].length; c++) {
            if (!shape[r][c]) continue
            const x = this.cur.x + c
            const y = this.cur.y + r
            if (y >= 0 && y < ROWS && x >= 0 && x < COLS) {
              view[y * COLS + x] = { v: color, ghost: false }
            }
            const gyAbs = gy + r
            if (gyAbs >= 0 && gyAbs < ROWS && x >= 0 && x < COLS) {
              const idx = gyAbs * COLS + x
              if (!view[idx].v) view[idx] = { v: color, ghost: true }
            }
          }
        }
      }
      return view
    }
  },
  created () {
    try {
      this.best = parseInt(localStorage.getItem(BEST_KEY), 10) || 0
    } catch (e) {
      this.best = 0
    }
    this.restart()
  },
  mounted () {
    window.addEventListener('keydown', this.onKey)
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.onKey)
    this.clearDrop()
  },
  methods: {
    /* ---------- 初始化 ---------- */
    restart () {
      this.board = Array(ROWS * COLS).fill(0)
      this.score = 0
      this.lines = 0
      this.level = 1
      this.over = false
      this.paused = false
      this.holdType = null
      this.canHold = true
      this.clearDrop()
      this.next = this.randomPiece()
      this.spawn()
      this.scheduleDrop()
    },
    randomPiece () {
      const type = TYPES[Math.floor(Math.random() * TYPES.length)]
      return { type, shape: SHAPES[type].map(row => row.slice()) }
    },
    updateBest () {
      if (this.score > this.best) {
        this.best = this.score
        try { localStorage.setItem(BEST_KEY, String(this.best)) } catch (e) {}
      }
    },

    /* ---------- 生成 / 碰撞 / 合并 ---------- */
    spawn () {
      this.cur = {
        type: this.next.type,
        shape: this.next.shape.map(row => row.slice()),
        x: 3,
        y: -2
      }
      this.next = this.randomPiece()
      this.canHold = true
      if (this.collide(this.cur.shape, this.cur.x, this.cur.y)) {
        this.over = true
        this.clearDrop()
        this.updateBest()
      }
    },
    collide (shape, x, y) {
      for (let r = 0; r < shape.length; r++) {
        for (let c = 0; c < shape[r].length; c++) {
          if (!shape[r][c]) continue
          const bx = x + c
          const by = y + r
          if (bx < 0 || bx >= COLS || by >= ROWS) return true
          if (by >= 0 && this.board[by * COLS + bx]) return true
        }
      }
      return false
    },
    lock () {
      const shape = this.cur.shape
      const color = COLOR[this.cur.type]
      for (let r = 0; r < shape.length; r++) {
        for (let c = 0; c < shape[r].length; c++) {
          if (!shape[r][c]) continue
          const y = this.cur.y + r
          const x = this.cur.x + c
          if (y >= 0 && y < ROWS && x >= 0 && x < COLS) {
            this.$set(this.board, y * COLS + x, color)
          }
        }
      }
      this.clearLines()
      if (!this.over) this.spawn()
    },

    /* ---------- 消行 / 计分 ---------- */
    clearLines () {
      const kept = []
      let cleared = 0
      for (let r = 0; r < ROWS; r++) {
        const row = this.board.slice(r * COLS, (r + 1) * COLS)
        if (row.every(v => v)) {
          cleared++
        } else {
          kept.push(...row)
        }
      }
      if (!cleared) return
      this.lines += cleared
      this.score += SCORE_LINE[cleared]
      this.level = Math.floor(this.lines / 10) + 1
      const empty = Array(cleared * COLS).fill(0)
      this.board = empty.concat(kept)
      this.scheduleDrop()
      this.updateBest()
    },

    /* ---------- 下落 ---------- */
    scheduleDrop () {
      this.clearDrop()
      const interval = Math.max(100, 750 - (this.level - 1) * 70)
      this.dropTimer = setInterval(() => this.tick(), interval)
    },
    clearDrop () {
      if (this.dropTimer) {
        clearInterval(this.dropTimer)
        this.dropTimer = null
      }
    },
    tick () {
      if (this.over || this.paused) return
      if (!this.collide(this.cur.shape, this.cur.x, this.cur.y + 1)) {
        this.cur.y++
      } else {
        this.lock()
      }
    },
    dropDistance () {
      let y = this.cur.y
      while (!this.collide(this.cur.shape, this.cur.x, y + 1)) {
        y++
      }
      return y
    },

    /* ---------- 操作 ---------- */
    onKey (e) {
      if (this.over) return
      const k = e.key
      if (k === 'ArrowLeft' || k === 'a' || k === 'A') {
        e.preventDefault()
        this.move(-1)
      } else if (k === 'ArrowRight' || k === 'd' || k === 'D') {
        e.preventDefault()
        this.move(1)
      } else if (k === 'ArrowDown' || k === 's' || k === 'S') {
        e.preventDefault()
        this.softDrop()
      } else if (k === 'ArrowUp' || k === 'w' || k === 'W') {
        e.preventDefault()
        this.rotateCur()
      } else if (k === ' ') {
        e.preventDefault()
        this.hardDrop()
      } else if (k === 'c' || k === 'C') {
        e.preventDefault()
        this.holdPiece()
      } else if (k === 'p' || k === 'P') {
        e.preventDefault()
        this.togglePause()
      }
    },
    move (dx) {
      if (this.over || this.paused) return
      const nx = this.cur.x + dx
      if (!this.collide(this.cur.shape, nx, this.cur.y)) {
        this.cur.x = nx
      }
    },
    softDrop () {
      if (this.over || this.paused) return
      if (!this.collide(this.cur.shape, this.cur.x, this.cur.y + 1)) {
        this.cur.y++
        this.score += 1
        this.updateBest()
      } else {
        this.lock()
        this.scheduleDrop()
      }
    },
    hardDrop () {
      if (this.over || this.paused) return
      while (!this.collide(this.cur.shape, this.cur.x, this.cur.y + 1)) {
        this.cur.y++
        this.score += 2
      }
      this.updateBest()
      this.lock()
      this.scheduleDrop()
    },
    rotateCur () {
      if (this.over || this.paused) return
      const rotated = this.rotate(this.cur.shape)
      const kicks = [0, -1, 1, -2, 2]
      for (const kx of kicks) {
        if (!this.collide(rotated, this.cur.x + kx, this.cur.y)) {
          this.cur.shape = rotated
          this.cur.x += kx
          return
        }
      }
    },
    rotate (m) {
      const n = m.length
      const res = Array.from({ length: n }, () => Array(n).fill(0))
      for (let r = 0; r < n; r++) {
        for (let c = 0; c < n; c++) {
          res[c][n - 1 - r] = m[r][c]
        }
      }
      return res
    },
    holdPiece () {
      if (this.over || this.paused || !this.canHold) return
      const curType = this.cur.type
      if (this.holdType === null) {
        this.holdType = curType
        this.spawn()
      } else {
        const swap = this.holdType
        this.holdType = curType
        this.cur = {
          type: swap,
          shape: SHAPES[swap].map(row => row.slice()),
          x: 3,
          y: -2
        }
        if (this.collide(this.cur.shape, this.cur.x, this.cur.y)) {
          this.over = true
          this.clearDrop()
          this.updateBest()
          return
        }
      }
      this.canHold = false
    },
    togglePause () {
      if (this.over) return
      this.paused = !this.paused
      if (this.paused) {
        this.clearDrop()
      } else {
        this.scheduleDrop()
      }
    }
  }
}
</script>

<style scoped>
.tet {
  font-family: "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
  color: #6b4a5a;
  max-width: 520px;
  margin: 0 auto;
}
.tet-inner {
  display: flex;
  gap: 16px;
  justify-content: center;
}

/* ---------- 游戏板 ---------- */
.tet-board {
  position: relative;
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 3px;
  width: 260px;
  padding: 10px;
  border-radius: 16px;
  background: linear-gradient(170deg, #ffe9f3, #ffd3e6);
  border: 2px solid #ffc3da;
  box-shadow: 0 10px 28px rgba(255, 158, 198, 0.3);
}
.tet-cell {
  aspect-ratio: 0.5;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.42);
  box-shadow: inset 0 0 0 1px rgba(255, 195, 218, 0.5);
}
.tet-cell.c1 { background: linear-gradient(135deg, #ffb3d4, #ff7ab8); box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.4), 0 2px 6px rgba(255, 122, 184, 0.45); }
.tet-cell.c2 { background: linear-gradient(135deg, #ffd98a, #ffb04d); box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.4), 0 2px 6px rgba(255, 176, 77, 0.45); }
.tet-cell.c3 { background: linear-gradient(135deg, #c9a6ff, #9b6dff); box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.4), 0 2px 6px rgba(155, 109, 255, 0.45); }
.tet-cell.c4 { background: linear-gradient(135deg, #8fe3c9, #4fc6a4); box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.4), 0 2px 6px rgba(79, 198, 164, 0.45); }
.tet-cell.c5 { background: linear-gradient(135deg, #7ec8ff, #4da3f0); box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.4), 0 2px 6px rgba(77, 163, 240, 0.45); }
.tet-cell.c6 { background: linear-gradient(135deg, #ffab9e, #ff7d6b); box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.4), 0 2px 6px rgba(255, 125, 107, 0.45); }
.tet-cell.c7 { background: linear-gradient(135deg, #ffb3e6, #f26ab6); box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.4), 0 2px 6px rgba(242, 106, 182, 0.45); }
.tet-cell.ghost {
  background: transparent;
  box-shadow: inset 0 0 0 2px rgba(255, 122, 184, 0.55);
}
.tet-cell.ghost.g1 { box-shadow: inset 0 0 0 2px rgba(255, 122, 184, 0.55); }
.tet-cell.ghost.g2 { box-shadow: inset 0 0 0 2px rgba(255, 176, 77, 0.55); }
.tet-cell.ghost.g3 { box-shadow: inset 0 0 0 2px rgba(155, 109, 255, 0.55); }
.tet-cell.ghost.g4 { box-shadow: inset 0 0 0 2px rgba(79, 198, 164, 0.55); }
.tet-cell.ghost.g5 { box-shadow: inset 0 0 0 2px rgba(77, 163, 240, 0.55); }
.tet-cell.ghost.g6 { box-shadow: inset 0 0 0 2px rgba(255, 125, 107, 0.55); }
.tet-cell.ghost.g7 { box-shadow: inset 0 0 0 2px rgba(242, 106, 182, 0.55); }

/* ---------- 结束/暂停遮罩 ---------- */
.tet-over {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: rgba(255, 240, 247, 0.92);
  backdrop-filter: blur(2px);
  border-radius: 14px;
  z-index: 5;
}
.tet-over-emoji { font-size: 42px; }
.tet-over-text {
  font-size: 18px;
  font-weight: 800;
  color: #e25b96;
}
.tet-over-score {
  font-size: 13px;
  color: #b06a8a;
}

/* ---------- 侧栏 ---------- */
.tet-side {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 110px;
}
.tet-hold, .tet-next {
  padding: 10px;
  border-radius: 14px;
  background: #fff7fb;
  border: 1px solid #ffddea;
  text-align: center;
}
.tet-hold.locked {
  opacity: 0.75;
}
.tet-side-label {
  font-size: 12px;
  color: #c490a8;
  margin-bottom: 8px;
}
.tet-next-grid {
  display: grid;
  grid-template-columns: repeat(4, 20px);
  gap: 3px;
  justify-content: center;
}
.tet-mini-cell {
  width: 20px;
  height: 20px;
  border-radius: 3px;
  background: rgba(255, 195, 218, 0.25);
}
.tet-mini-cell.c1 { background: #ff7ab8; }
.tet-mini-cell.c2 { background: #ffb04d; }
.tet-mini-cell.c3 { background: #9b6dff; }
.tet-mini-cell.c4 { background: #4fc6a4; }
.tet-mini-cell.c5 { background: #4da3f0; }
.tet-mini-cell.c6 { background: #ff7d6b; }
.tet-mini-cell.c7 { background: #f26ab6; }
.tet-stat {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 12px;
  background: #fff7fb;
  border: 1px solid #ffddea;
  font-size: 13px;
  color: #b06a8a;
}
.tet-stat b {
  font-size: 16px;
  color: #ff5fa0;
}
.tet-btn {
  border: none;
  border-radius: 999px;
  padding: 9px 18px;
  background: linear-gradient(135deg, #ff8fc7, #ff6fa5);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(255, 111, 165, 0.35);
  transition: transform 0.2s ease;
}
.tet-btn:hover { transform: translateY(-2px); }
.tet-restart {
  background: #fff;
  border: 2px solid #ffd3e4;
  color: #b06a8a;
  box-shadow: none;
}
.tet-restart:hover { border-color: #ff6fa5; color: #e25b96; }

/* ---------- 控制面板 ---------- */
.tet-pad {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
}
.tet-pad-btn {
  min-width: 56px;
  border: 2px solid #ffd3e4;
  border-radius: 12px;
  background: #fff;
  color: #e25b96;
  font-size: 15px;
  font-weight: 700;
  padding: 9px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.tet-pad-btn:hover {
  border-color: #ff6fa5;
  background: #ffeef6;
  transform: translateY(-2px);
}
.tet-tip {
  text-align: center;
  font-size: 12px;
  color: #c490a8;
  margin-top: 10px;
}
</style>
